import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import { BTLocalStorage } from "types/btStorage";

import { reportError, reportMessage } from "utilities/errorHelpers";

import { formatBytes } from "commonComponents/utilities/ByteDisplay/ByteDisplay.utilities";

interface ISawmillOptions {
    tenantId: string;
    ingestionEndpoint: string;
    batchThreshold: number;
    maxCachedEvents: number;
    maxPayloadSizeBytes: number;
}

interface ISawmillRequest {
    tenantId: string;
    idempotencyKey?: string;
    events: ISawmillEvent[];
}

export interface ISawmillEvent {
    client: string;
    clientOS: string;
    userId: string;
    eventName: string;
    timestamp: string;
    body: any;
}

export function sawmillPlugin(userConfig: ISawmillOptions) {
    return {
        name: "sawmill",
        config: { ...userConfig },
        track: async ({ payload, config }: { payload: any; config: ISawmillOptions }) => {
            const newEvent = {
                client: "Browser",
                clientOS: window.navigator.userAgent || "unknown",
                userId: payload.userId,
                eventName: payload.event,
                timestamp: moment.utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
                body: payload.properties,
            };

            // ado-178532 we had massive event sizes put on this key which made it unsafe
            // Remove after some time
            BTLocalStorage.remove("bt-objectArray-sawmillEvents");

            await navigator.locks.request("sawmillEvents", { steal: false }, async () => {
                const cachedEvents = BTLocalStorage.get("bt-objectArray-sawmillEventsV2");
                const events: ISawmillEvent[] = [...cachedEvents, newEvent];
                const formatBytesStr = (n: number) => {
                    const { value, label } = formatBytes(n);
                    return `${value} ${label}`;
                };

                /**
                 * Build a one-line summary of the queue contents at overflow time so the
                 * `reportMessage` lands in Sentry with enough context to distinguish
                 * "queue stuck for days on one user" from "one bloated event" from
                 * "burst of activity in this session." Includes:
                 *  - oldest event's age (seconds since timestamp)
                 *  - top-5 event names by frequency
                 *  - largest single event's name + size
                 *
                 * Costs one extra O(N) pass; only runs on the overflow path, which is
                 * rare and already paying for stringify + reportMessage anyway.
                 */
                const buildQueueDiagnostics = (queue: ISawmillEvent[], sizes: number[]): string => {
                    if (queue.length === 0) return "";
                    const now = Date.now();
                    let oldestTs = now;
                    const nameCounts: Record<string, number> = {};
                    let largestIdx = 0;
                    for (let i = 0; i < queue.length; i++) {
                        const ev = queue[i];
                        const parsed = Date.parse(ev.timestamp);
                        if (!isNaN(parsed) && parsed < oldestTs) oldestTs = parsed;
                        nameCounts[ev.eventName] = (nameCounts[ev.eventName] ?? 0) + 1;
                        if (sizes[i] > sizes[largestIdx]) largestIdx = i;
                    }
                    const oldestAgeSec = Math.max(0, Math.round((now - oldestTs) / 1000));
                    const topNames = Object.entries(nameCounts)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 5)
                        .map(([n, c]) => `${n}=${c}`)
                        .join(", ");
                    const largest = queue[largestIdx];
                    return (
                        ` Oldest event age: ${oldestAgeSec}s.` +
                        ` Top events: ${topNames}.` +
                        ` Largest event: "${largest.eventName}" at ${formatBytesStr(
                            sizes[largestIdx],
                        )}.`
                    );
                };

                // Per-event sizes (+1 for the joining comma); +2 for the wrapping brackets.
                // This is an approximation of JSON.stringify(events).length that lets us trim
                // in O(N) without re-stringifying the array after each drop.
                const eventSizes = events.map((e) => JSON.stringify(e).length + 1);
                let totalSize = eventSizes.reduce((s, n) => s + n, 0) + 2;

                if (totalSize > config.maxPayloadSizeBytes) {
                    const newEventSize = eventSizes[eventSizes.length - 1];
                    if (newEventSize > config.maxPayloadSizeBytes) {
                        // Single new event is itself oversized — drop only it; preserve the
                        // existing queue so cached events still have a chance to flush.
                        BTLocalStorage.set("bt-objectArray-sawmillEventsV2", cachedEvents);
                        if (config.maxPayloadSizeBytes > 0) {
                            const cachedSizes = eventSizes.slice(0, -1);
                            reportMessage(
                                `Sawmill event "${newEvent.eventName}" is ${formatBytesStr(
                                    newEventSize,
                                )}, exceeding the ${formatBytesStr(
                                    config.maxPayloadSizeBytes,
                                )} max payload. Dropping this event; ${
                                    cachedEvents.length
                                } cached event(s) preserved.` +
                                    buildQueueDiagnostics(cachedEvents, cachedSizes),
                            );
                        }
                        return;
                    }

                    // Queue grew past the cap (typically from accumulated send failures).
                    // Trim oldest events until we're under the cap — preserves the newest
                    // events instead of dropping everything.
                    const originalCount = events.length;
                    while (events.length > 1 && totalSize > config.maxPayloadSizeBytes) {
                        totalSize -= eventSizes.shift()!;
                        events.shift();
                    }
                    const droppedCount = originalCount - events.length;
                    if (config.maxPayloadSizeBytes > 0 && droppedCount > 0) {
                        // Diagnostics are computed against the ORIGINAL queue (pre-trim)
                        // so the Sentry message shows what was actually piling up.
                        const originalQueue = [...cachedEvents, newEvent];
                        const originalSizes = originalQueue.map(
                            (e) => JSON.stringify(e).length + 1,
                        );
                        reportMessage(
                            `Sawmill queue exceeded ${formatBytesStr(
                                config.maxPayloadSizeBytes,
                            )} max payload. Trimmed ${droppedCount} oldest event(s); kept ${
                                events.length
                            } (${formatBytesStr(totalSize)}).` +
                                buildQueueDiagnostics(originalQueue, originalSizes),
                        );
                    }
                }

                if (events.length >= config.batchThreshold) {
                    const idempotencyKey = uuidv4();
                    try {
                        const response = await sendSawmillRequest(idempotencyKey, config, events);
                        if (response.ok) {
                            BTLocalStorage.set("bt-objectArray-sawmillEventsV2", []);
                            return;
                        }
                    } catch (error) {
                        // waiting on https://github.com/getsentry/sentry-javascript/issues/4146 for more debug info
                        reportError(
                            error,
                            {
                                internalNotes:
                                    "Failed to post Sawmill events. The queue will be tried again on the next event. Idempotency key: " +
                                    idempotencyKey,
                            },
                            "warning",
                        );
                    }
                }

                BTLocalStorage.set(
                    "bt-objectArray-sawmillEventsV2",
                    events.slice(-config.maxCachedEvents),
                );
            });
        },
    };
}

function sendSawmillRequest(
    idempotencyKey: string,
    config: ISawmillOptions,
    events: ISawmillEvent[],
) {
    const request = {
        tenantId: config.tenantId,
        idempotencyKey: idempotencyKey,
        events,
    } as ISawmillRequest;

    return fetch(config.ingestionEndpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(request),
    });
}
